import React, { ChangeEvent, useState } from "react";

import check from "@/assets/images/check2-min.svg";
import { cn, isNumber } from "@/utils/utils";
import SimpleErrorMessage from "../error-message/new-error-message";
import { Label } from "./label";
import { Icon } from "@/components/ui/Icon"

type InputProps = {
  labelText: string;
  isError?: boolean;
  errorMsg?: string;
  errorMsgBlocked?: string;
  isFilled?: boolean;
  type?: "password" | "text";
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  value?: string;
  disabled?: boolean;
  showCheckIcon?: boolean;
  onBlur?: (event: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (value: boolean) => void;
  onHover?: (value: boolean) => void;
  onClick?: () => void;
  showError?: boolean;
  autoComplete?: string;
  onKeyPress?: (e: React.KeyboardEvent) => void;
  isDateInput?: boolean;
  placeholder?: string;
  name?: string;
  id?: string;
  ariaDescribedBy?: string;
  ariaLabel?: string;
  ariaInvalid?: boolean;
  ariaDisabled?: boolean;
  ariaHidden?: boolean;
  autofocus?: boolean;
  tabIndex?: number;
  ariaReadonly?: boolean;
  ariaErrorMessage?: string
  inputMode?:
  | "text"
  | "search"
  | "email"
  | "tel"
  | "url"
  | "none"
  | "numeric"
  | "decimal"
  | undefined;
  required?: boolean;
  onInvalid?: (e: any) => void;
  category?:
  | "text"
  | "phone"
  | "cardNumber"
  | "pin";
  showDropdown?: boolean;
};

export default function Input({
  showError = true,
  inputMode = "text",
  required = false,
  autofocus = false,
  tabIndex,
  ariaReadonly,
  category = "text",
  type = "text",
  showDropdown = false,
  ...props
}: InputProps) {

  if (props.isFilled == undefined) {
    props.isFilled = props.value != null && props.value !== "";
  }

  const [isHidden, setIsHidden] = useState<boolean>(type === "password");
  const clickEye = () => {
    setIsHidden((state) => {
      return !state;
    });
  };

  return (
    <div className={'w-full container-input group flex flex-col relative'}>
      <Label
        className={showDropdown ? "-ml-36" : ""}
        disabled={props.disabled}
        required={required}
        htmlFor={props.id}>
        {props.labelText}
      </Label>
      <input
        id={props.id}
        max={6}
        name={props.name ?? props.id}
        placeholder={props.placeholder}
        title={props.labelText}
        autoFocus={autofocus}
        type={!isHidden ? "text" : type}
        className={cn(
          "focus:outline-none",
          "bg-[var(--surface-default)]",
          "text-[var(--on-surface-variant-2)]",
          "border-[1px] border-[var(--outline-default)]",
          "h-24 pl-5 pr-12 w-full text-base",
          "disabled:bg-[var(--surface-disabled)] disabled:border-[var(--outline-disabled)]",
          showDropdown ?
            "pl-0 border-l-[0] rounded-r-lg rounded-l-none" :
            "rounded-lg"
        )}
        value={props.value}
        onChange={props.onChange}
        disabled={props.disabled}
        onClick={props.onClick}
        onKeyPress={props.onKeyPress}
        aria-label={props.ariaLabel}
        aria-disabled={props.ariaDisabled}
        aria-describedby={props.ariaDescribedBy}
        aria-invalid={props.ariaInvalid}
        aria-errormessage={props.ariaErrorMessage}
        aria-readonly={ariaReadonly}
        aria-hidden={props.ariaHidden}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (props.isDateInput) {
            e.preventDefault();
          }
        }}
        autoComplete={props.autoComplete}
        inputMode={inputMode}
        required={required}
        tabIndex={tabIndex}
        onInvalid={(e: React.InvalidEvent<HTMLInputElement>) => {
          if (props.onInvalid) {
            e.preventDefault()
            if (e.target.validity.valueMissing) {
              props.onInvalid(e)
            }
          }
        }}
      />

      {
        showError &&
        <SimpleErrorMessage
          id={props.id + "__error-message"}
          error={props.errorMsg}
          className={cn(category === "phone" && isNumber(props.value) && "-ml-32")}
        />
      }

      {type === "password" && (
        <div className="absolute right-5 top-16 cursor-pointer">
          <Icon name={isHidden ?
            "StreamlineEssentialViewOffBold" :
            "StreamlineEssentialView1Bold"}
            size={"md"}
            className="text-[var(--outline-default)]"
            onClick={clickEye}
          />
        </div>
      )}
      {props.showCheckIcon && (
        <img src={check} alt="" className="absolute right-5 top-4 w-4 cursor-pointer" />
      )}
    </div>
  );
}
